import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminFormDialog } from '@/components/shared/AdminFormDialog';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import { apiClient } from '@/services/api-client';
import { parseCommEmailBrand } from '@shared/email/commEmailView';
import { TEMPLATE_CHANNELS } from './commCatalog';
import {
    buildTemplatePreview,
    PREVIEW_EXPERIENCE_LEVELS,
    PREVIEW_WATER_TYPES,
    templateUsesA0Fragments,
    type PreviewTemplateRow,
} from './commPreview';
import { CopyGuide } from './commCopyHelp';
import { DestinationField } from './DestinationField';
import { isRegistrationTemplate, sanitizeCommKey, type TemplateDraft } from './commUi';

type RealPreview = {
    subject: string;
    preheader: string | null;
    html: string;
    userLabel: string;
    userId: string;
};

export function TemplateEditorDialog(props: {
    creating: boolean;
    draft: TemplateDraft;
    templates: PreviewTemplateRow[];
    saving: boolean;
    error?: string | null;
    onChange: (next: TemplateDraft) => void;
    onCancel: () => void;
    onSave: () => void;
}) {
    const { creating, draft, templates, saving, error, onChange, onCancel, onSave } = props;
    const [initial] = useState(draft);
    const [experienceLevel, setExperienceLevel] = useState('intermedio');
    const [waterType, setWaterType] = useState('ambas');
    const [userQuery, setUserQuery] = useState('');
    const [previewUserId, setPreviewUserId] = useState<string | null>(null);
    const [realPreview, setRealPreview] = useState<RealPreview | null>(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [testEmail, setTestEmail] = useState('');
    const [testBusy, setTestBusy] = useState(false);
    const [testMsg, setTestMsg] = useState<string | null>(null);
    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(initial), [draft, initial]);
    const usesA0Fragments = templateUsesA0Fragments(draft.body);
    const system = isRegistrationTemplate(draft.key);
    const brandQuery = useQuery({
        queryKey: ['admin', 'comm-email-brand'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc<
                Array<{ logo_url: string | null; header_color: string | null; signature: string | null; footer_text: string | null }>
            >('admin_get_comm_email_brand');
            if (error) throw error;
            const row = normalizeRpcResponse(data)[0];
            return parseCommEmailBrand(
                row
                    ? {
                          logo_url: row.logo_url || '',
                          header_color: row.header_color || '',
                          signature: row.signature || '',
                          footer_text: row.footer_text || '',
                      }
                    : null
            );
        },
    });
    const samplePreview = useMemo(
        () =>
            buildTemplatePreview({
                key: draft.key,
                channel: draft.channel,
                locale: draft.locale,
                variant: draft.variant,
                subject: draft.subject,
                preheader: draft.preheader,
                body: draft.body,
                clickUrl: draft.click_url,
                templates,
                experienceLevel,
                waterType,
                brand: brandQuery.data,
            }),
        [
            draft.key,
            draft.channel,
            draft.locale,
            draft.variant,
            draft.subject,
            draft.preheader,
            draft.body,
            draft.click_url,
            templates,
            experienceLevel,
            waterType,
            brandQuery.data,
        ]
    );

    const previewSubject = realPreview?.subject ?? samplePreview.subject;
    const previewPreheader = realPreview?.preheader ?? samplePreview.preheader;
    const previewHtml = realPreview?.html ?? samplePreview.html;
    const previewMissing = realPreview ? [] : samplePreview.missing;

    const set = (patch: Partial<TemplateDraft>) => onChange({ ...draft, ...patch });

    async function loadRealPreview() {
        if (!draft.key || creating) {
            setPreviewError('Guarda la plantilla antes de previsualizar con un usuario real.');
            return;
        }
        setPreviewBusy(true);
        setPreviewError(null);
        setTestMsg(null);
        try {
            const res = await apiClient.post<{
                ok?: boolean;
                reason?: string;
                missing?: string[];
                subject?: string;
                preheader?: string | null;
                html?: string;
                user_id?: string;
                user_label?: string;
            }>('/ops/comm-template-preview', {
                action: 'preview',
                template_key: draft.key,
                user_id: previewUserId || undefined,
                query: previewUserId ? undefined : userQuery.trim() || undefined,
            });
            if (res.error) throw new Error(res.error.message || 'Error de preview');
            const data = res.data;
            if (!data?.ok) {
                setRealPreview(null);
                setPreviewError(
                    `No se pudo renderizar: ${data?.reason || 'error'}${
                        data?.missing?.length ? ` (${data.missing.join(', ')})` : ''
                    }`
                );
                return;
            }
            setPreviewUserId(data.user_id || null);
            setRealPreview({
                subject: data.subject || '',
                preheader: data.preheader ?? null,
                html: data.html || '',
                userLabel: data.user_label || 'usuario',
                userId: data.user_id || '',
            });
        } catch (err) {
            setPreviewError(err instanceof Error ? err.message : 'Error de preview');
        } finally {
            setPreviewBusy(false);
        }
    }

    async function sendTest() {
        if (!draft.key || creating || !previewUserId) {
            setTestMsg('Carga primero la vista previa de un usuario real.');
            return;
        }
        if (!testEmail.trim().includes('@')) {
            setTestMsg('Indica un email de equipo.');
            return;
        }
        setTestBusy(true);
        setTestMsg(null);
        try {
            const res = await apiClient.post<{ ok?: boolean; delivery_id?: string }>(
                '/ops/comm-template-preview',
                {
                    action: 'send_test',
                    template_key: draft.key,
                    user_id: previewUserId,
                    to_email: testEmail.trim(),
                }
            );
            if (res.error) throw new Error(res.error.message || 'Error al enviar');
            setTestMsg(`Prueba enviada (${res.data?.delivery_id || 'ok'}). No cuenta en cupos de regla.`);
        } catch (err) {
            setTestMsg(err instanceof Error ? err.message : 'Error al enviar');
        } finally {
            setTestBusy(false);
        }
    }

    return (
        <AdminFormDialog
            open
            title={creating ? 'Nueva plantilla' : `${draft.key} · ${draft.channel}`}
            description={
                <>
                    Texto plano. Variables {'{nombre}'}, {'{bloque:cifras_mes}'}, etc. Un botón en su propia
                    línea: [Texto del botón]. No se guarda HTML.
                </>
            }
            dirty={dirty}
            saving={saving}
            error={error}
            saveDisabled={(creating && !draft.key) || !draft.body.trim()}
            maxWidthClass="w-[min(96rem,96vw)] max-w-[min(96rem,96vw)]"
            contentClassName="flex h-[min(92vh,980px)] max-h-[92vh] flex-col overflow-hidden p-6"
            bodyClassName="min-h-0 flex-1 space-y-0 overflow-hidden"
            onClose={onCancel}
            onSave={onSave}
        >
            <div className="grid h-full min-h-0 grid-cols-1 gap-6 lg:grid-cols-2">
                <div className="min-h-0 min-w-0 space-y-3 overflow-y-auto pr-1">
                    {creating && (
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block text-sm col-span-2">
                                Clave
                                <input
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 font-mono text-sm"
                                    value={draft.key}
                                    onChange={(e) => set({ key: sanitizeCommKey(e.target.value) })}
                                    placeholder="retencion_d7"
                                />
                            </label>
                            <label className="block text-sm">
                                Canal
                                <select
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                    value={draft.channel}
                                    onChange={(e) => set({ channel: e.target.value })}
                                >
                                    {TEMPLATE_CHANNELS.map((c) => (
                                        <option key={c.key} value={c.key}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block text-sm">
                                Variante
                                <input
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                    value={draft.variant}
                                    onChange={(e) => set({ variant: e.target.value || 'A' })}
                                />
                            </label>
                        </div>
                    )}
                    <label className="block text-sm">
                        Asunto
                        <input
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                            value={draft.subject}
                            onChange={(e) =>
                                set({ subject: e.target.value, title: e.target.value })
                            }
                        />
                    </label>
                    <label className="block text-sm">
                        Preheader
                        <input
                            className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                            value={draft.preheader}
                            onChange={(e) => set({ preheader: e.target.value })}
                        />
                    </label>
                    <DestinationField
                        label="Destino"
                        value={draft.click_url}
                        onChange={(click_url) => set({ click_url })}
                    />
                    <label className="block text-sm">
                        Cuerpo
                        <textarea
                            className="mt-1 w-full min-h-[280px] rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono"
                            value={draft.body}
                            onChange={(e) => set({ body: e.target.value })}
                        />
                    </label>
                    <CopyGuide channel={draft.channel === 'push' ? 'push' : 'email'} />
                    {system ? (
                        <p className="text-sm text-muted-foreground">
                            Este mensaje es del registro: puedes editar el texto aquí. Para pausar o reanudar el
                            envío, usa Reglas → Del registro (no este interruptor).
                        </p>
                    ) : (
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={draft.is_active}
                                onChange={(e) => set({ is_active: e.target.checked })}
                            />
                            Activa
                        </label>
                    )}
                </div>
                <div className="flex min-h-0 min-w-0 flex-col">
                    <p className="mb-2 shrink-0 text-sm font-medium">Vista previa</p>
                    {draft.channel === 'email' && !creating && (
                        <div className="mb-3 space-y-2 rounded-lg border border-border bg-muted/30 p-3 shrink-0">
                            <p className="text-xs font-medium text-foreground">Usuario real</p>
                            <div className="flex flex-wrap gap-2">
                                <input
                                    className="min-w-[12rem] flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
                                    placeholder="email, username o UUID"
                                    value={userQuery}
                                    onChange={(e) => {
                                        setUserQuery(e.target.value);
                                        setPreviewUserId(null);
                                        setRealPreview(null);
                                    }}
                                />
                                <button
                                    type="button"
                                    className="rounded-lg bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
                                    disabled={previewBusy || (!userQuery.trim() && !previewUserId)}
                                    onClick={() => void loadRealPreview()}
                                >
                                    {previewBusy ? 'Cargando…' : 'Renderizar'}
                                </button>
                                {realPreview && (
                                    <button
                                        type="button"
                                        className="rounded-lg border border-border px-3 py-1.5 text-sm"
                                        onClick={() => {
                                            setRealPreview(null);
                                            setPreviewUserId(null);
                                        }}
                                    >
                                        Ejemplo
                                    </button>
                                )}
                            </div>
                            {realPreview && (
                                <p className="text-xs text-muted-foreground">
                                    Datos de {realPreview.userLabel} ({realPreview.userId.slice(0, 8)}…)
                                </p>
                            )}
                            {previewError && <p className="text-xs text-amber-500">{previewError}</p>}
                            <div className="flex flex-wrap gap-2 pt-1">
                                <input
                                    className="min-w-[12rem] flex-1 rounded-lg border border-border bg-background px-3 py-1.5 text-sm"
                                    placeholder="Email de equipo para prueba"
                                    value={testEmail}
                                    onChange={(e) => setTestEmail(e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="rounded-lg border border-border px-3 py-1.5 text-sm disabled:opacity-50"
                                    disabled={testBusy || !previewUserId}
                                    onClick={() => void sendTest()}
                                >
                                    {testBusy ? 'Enviando…' : 'Enviarme una prueba'}
                                </button>
                            </div>
                            {testMsg && <p className="text-xs text-muted-foreground">{testMsg}</p>}
                        </div>
                    )}
                    {usesA0Fragments && (
                        <div className="mb-3 grid shrink-0 grid-cols-2 gap-3">
                            <label className="block text-sm">
                                Nivel
                                <select
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                    value={experienceLevel}
                                    onChange={(e) => setExperienceLevel(e.target.value)}
                                >
                                    {PREVIEW_EXPERIENCE_LEVELS.map((level) => (
                                        <option key={level} value={level}>
                                            {level}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="block text-sm">
                                Tipo de agua
                                <select
                                    className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2"
                                    value={waterType}
                                    onChange={(e) => setWaterType(e.target.value)}
                                >
                                    {PREVIEW_WATER_TYPES.map((water) => (
                                        <option key={water} value={water}>
                                            {water}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <p className="col-span-2 text-xs text-muted-foreground">
                                El destinatario recibe una de las nueve combinaciones. El asunto
                                sale de la plantilla activacion_a0.subject.{experienceLevel}.
                            </p>
                        </div>
                    )}
                    {previewMissing.length > 0 && (
                        <p className="mb-2 shrink-0 text-sm text-amber-400">
                            Variables sin valor: {previewMissing.join(', ')}
                        </p>
                    )}
                    <div
                        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-slate-200"
                        style={{ colorScheme: 'light', background: '#e2e8f0' }}
                    >
                        <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-2.5">
                            <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                                Asunto
                            </p>
                            <p className="text-sm font-semibold text-slate-900">{previewSubject}</p>
                            {previewPreheader ? (
                                <p className="truncate text-xs text-slate-500">{previewPreheader}</p>
                            ) : null}
                        </div>
                        <div className="relative min-h-0 flex-1">
                            <iframe
                                title="Vista previa del correo"
                                srcDoc={previewHtml}
                                sandbox=""
                                referrerPolicy="no-referrer"
                                className="absolute inset-0 h-full w-full border-0 bg-[#f8fafc]"
                                style={{ colorScheme: 'light' }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </AdminFormDialog>
    );
}
